// Client wrappers for recruiter-initiated candidate actions (served same-origin at
// /api/recruiter/*). Thin fetch helpers that forward the PocketBase session token
// so the sidecar can authorize the recruiter and re-derive everything server-side.
//
// These moved server-side when candidates/billing were locked to the sidecar
// (1700000280): the browser can no longer write those collections, so the unlock
// meter, the Live Vouch "waiting"/"confirmed" state, and the device-wake all run
// through the api as superuser. Best-effort behaviours (a row with no linked app
// account) return a flag rather than failing.

import { onAuthExpired } from './pb'

const BASE = '/api/recruiter'

export interface LiveVouchRequestResult {
  ok: true
  delivered: boolean
}

const post = async (path: string, pbToken: string, payload: Record<string, unknown>): Promise<unknown> => {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(pbToken ? { 'X-PB-Token': pbToken } : {}) },
    body: JSON.stringify(payload),
  })
  const data: unknown = await res.json().catch(() => ({}))
  if (res.status === 401) onAuthExpired()
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Request failed.')
  return data
}

/** Recruiter+: notify a candidate's device that a Live Vouch was requested. Also
 *  persists the recruiter-facing "waiting" state on the candidate row server-side. */
export async function requestLiveVouchPush(
  candidateId: string,
  pbToken: string,
  checkpoint?: string,
): Promise<LiveVouchRequestResult> {
  return (await post('/live-vouch/request', pbToken, { candidateId, checkpoint })) as LiveVouchRequestResult
}

/** Recruiter+: meter + reveal a verified-pool profile. The cap and the billing
 *  increment are re-derived server-side, so it can't be reset from devtools
 *  (C01 / C05). Throws on a permission/cap error so the caller can roll back. */
export async function unlockCandidate(
  candidateId: string,
  pbToken: string,
): Promise<{ ok: true; used: number; unlocked: boolean }> {
  return (await post('/unlock', pbToken, { candidateId })) as { ok: true; used: number; unlocked: boolean }
}

/** SHA-256 of a file as lowercase hex, computed in THIS browser. The résumé itself
 *  is never uploaded — we send the digest alone, the same posture the candidate used
 *  when they signed it. */
export async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export interface ResumeCheckResult {
  ok: true
  /** Whether the recruiter's own copy hashes to the fingerprint on record. */
  match: 'matched' | 'mismatch' | 'unverified'
  fileName: string
  signedAt: string
  recordedHash: string
  /** Whether the candidate's holder key really signed that fingerprint, replayed
   *  server-side. `null` = no signing key stored with the record (pre-dates it), so
   *  the signature is genuinely UNKNOWN — never render that as a pass. */
  signatureValid: boolean | null
}

/** Recruiter+: check a résumé the recruiter received against the candidate's
 *  device-signed fingerprint. Pass the SHA-256 of the file, hashed in the
 *  recruiter's own browser — the document itself never leaves it. */
export async function checkResumeHash(
  candidateId: string,
  hash: string,
  pbToken: string,
): Promise<ResumeCheckResult> {
  return (await post('/resume/check', pbToken, { candidateId, hash })) as ResumeCheckResult
}

/** Recruiter+: confirm a Live Vouch on a DEMO pool row (no linked device). */
export async function confirmLiveVouchPush(
  candidateId: string,
  pbToken: string,
  opts?: { checkpoint?: string; deviceKeyB64?: string },
): Promise<{ ok: true; deviceChanged: boolean }> {
  return (await post('/live-vouch/confirm', pbToken, { candidateId, ...opts })) as {
    ok: true
    deviceChanged: boolean
  }
}
